import React, { useEffect, useState, useCallback, useRef } from "react";
import Modal from "react-bootstrap/Modal";
import ReactCrop, { centerCrop, makeAspectCrop } from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";

const OUTPUT_SIZE = 512;

function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = () => resolve(fr.result);
    fr.onerror = reject;
    fr.readAsDataURL(file);
  });
}

function centerSquareCrop(mediaWidth, mediaHeight) {
  const size = Math.min(mediaWidth, mediaHeight) * 0.9;
  return centerCrop(
    makeAspectCrop(
      { unit: "px", width: size },
      1,
      mediaWidth,
      mediaHeight
    ),
    mediaWidth,
    mediaHeight
  );
}

async function cropToBlob(img, crop) {
  const scaleX = img.naturalWidth / img.width;
  const scaleY = img.naturalHeight / img.height;

  const sx = Math.max(0, Math.round(crop.x * scaleX));
  const sy = Math.max(0, Math.round(crop.y * scaleY));
  const sWidth = Math.min(
    img.naturalWidth - sx,
    Math.round(crop.width * scaleX)
  );
  const sHeight = Math.min(
    img.naturalHeight - sy,
    Math.round(crop.height * scaleY)
  );

  const canvas = document.createElement("canvas");
  canvas.width = OUTPUT_SIZE;
  canvas.height = OUTPUT_SIZE;
  const ctx = canvas.getContext("2d");
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(img, sx, sy, sWidth, sHeight, 0, 0, OUTPUT_SIZE, OUTPUT_SIZE);

  return new Promise((resolve) =>
    canvas.toBlob((b) => resolve(b), "image/png", 0.95)
  );
}

const UpdateClientLogo = ({
  domain,
  show,
  clientId,
  handleClose,
  handleFormSubmit,
}) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [orgName, setOrgName] = useState("");
  const [currentLogoUrl, setCurrentLogoUrl] = useState("");

  const [imageSrc, setImageSrc] = useState("");
  const imgRef = useRef(null);

  const [crop, setCrop] = useState();
  const [completedCrop, setCompletedCrop] = useState();

  useEffect(() => {
    if (!show) {
      setImageSrc("");
      setCrop(undefined);
      setCompletedCrop(undefined);
      setSaving(false);
      setLoading(false);
    }
  }, [show]);

  useEffect(() => {
    if (!show || !clientId) return;
    setLoading(true);
    fetch(`/api/organizations/${clientId}`)
      .then(async (r) => {
        const t = await r.text();
        if (!r.ok) throw new Error(t || "Failed to load client");
        return t ? JSON.parse(t) : {};
      })
      .then((data) => {
        setOrgName(data?.organization || "");
        setCurrentLogoUrl(data?.logoUrl || data?.logo || "");
      })
      .catch((e) => {
        console.error(e);
        alert("Error loading client");
      })
      .finally(() => setLoading(false));
  }, [show, clientId]);

  const onSelectFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!/^image\/(png|jpe?g|webp|gif)$/i.test(file.type)) {
      alert("Please select a PNG, JPG, or WEBP image.");
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      alert("Image is too large. Max size is 8 MB.");
      return;
    }
    const dataUrl = await fileToDataUrl(file);
    setImageSrc(dataUrl);
    setCrop(undefined);
    setCompletedCrop(undefined);
  };

  const onImageLoaded = useCallback((img) => {
    imgRef.current = img;
    const initial = centerSquareCrop(img.width, img.height);
    setCrop(initial);
    setCompletedCrop(initial);
  }, []);

  const uploadLogo = async () => {
    if (!clientId) return alert("Missing client id");
    if (!imgRef.current || !completedCrop?.width || !completedCrop?.height) {
      return alert("Choose an image and adjust the crop first.");
    }

    setSaving(true);
    try {
      const blob = await cropToBlob(imgRef.current, completedCrop);
      const file = new File([blob], `logo-${clientId}.png`, { type: "image/png" });
      const form = new FormData();
      form.append("logo", file);

      const res = await fetch(`/api/organizations/${clientId}/logo`, {
        method: "POST",
        body: form,
      });
      const text = await res.text();
      if (!res.ok) throw new Error(text || "Failed to upload logo");

      handleFormSubmit?.();
      handleClose?.();
    } catch (err) {
      console.error("Upload logo error:", err);
      alert("Error uploading logo");
    } finally {
      setSaving(false);
    }
  };

  const deleteLogo = async () => {
    if (!clientId) return;
    if (!window.confirm("Remove the current logo?")) return;
    setSaving(true);
    try {
      const res = await fetch(`/api/organizations/${clientId}/logo`, {
        method: "DELETE",
      });
      const text = await res.text();
      if (!res.ok) throw new Error(text || "Failed to delete logo");
      handleFormSubmit?.();
      handleClose?.();
    } catch (err) {
      console.error("Delete logo error:", err);
      alert("Error deleting logo");
    } finally {
      setSaving(false);
    }
  };

  const canSave = !saving && !!imageSrc && !!completedCrop?.width;

  return (
    <>
      <style>{`
        .update-logo-modal .modal-content {
          background: #fff;
          border: 1px solid #e5e7eb;
          border-radius: 18px;
          box-shadow: 0 20px 60px rgba(0,0,0,0.12);
        }
        .update-logo-modal .modal-header {
          background: #fff;
          border-bottom: 1px solid #f3f4f6;
          border-radius: 18px 18px 0 0;
          padding: 20px 24px 16px;
        }
        .update-logo-modal .modal-body {
          background: #fff;
          padding: 20px 24px;
        }
        .update-logo-modal .modal-footer {
          background: #f9fafb;
          border-top: 1px solid #f3f4f6;
          border-radius: 0 0 18px 18px;
          padding: 16px 24px;
        }
        .logo-upload-zone {
          border: 1.5px dashed #d1d5db;
          border-radius: 12px;
          padding: 20px;
          text-align: center;
          cursor: pointer;
          transition: border-color 0.15s, background 0.15s;
          background: #fafafa;
        }
        .logo-upload-zone:hover {
          border-color: #ff5100;
          background: rgba(255,81,0,0.03);
        }
      `}</style>

      <Modal
        show={show}
        onHide={saving ? undefined : handleClose}
        centered
        size="md"
        dialogClassName="update-logo-modal"
        style={{ backdropFilter: "blur(3px)" }}
      >
        <Modal.Header closeButton={!saving}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div
              style={{
                width: 34, height: 34, borderRadius: 9,
                background: "rgba(255,81,0,0.08)",
                border: "1.5px solid rgba(255,81,0,0.2)",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}
            >
              <i className="fas fa-camera" style={{ color: "#ff5100", fontSize: 13 }} />
            </div>
            <div>
              <div style={{ fontWeight: 800, fontSize: 16, color: "#111827" }}>Update Logo</div>
              <div style={{ fontSize: 12, color: "#9ca3af", marginTop: 1 }}>
                {orgName ? orgName : "Upload a square image"}
              </div>
            </div>
          </div>
        </Modal.Header>

        <Modal.Body>
          {loading ? (
            <div style={{ textAlign: "center", padding: "32px 0", color: "#9ca3af" }}>
              <i className="fas fa-spinner fa-spin" style={{ fontSize: 22, marginBottom: 10, display: "block" }} />
              <div style={{ fontSize: 13, fontWeight: 600 }}>Loading client…</div>
            </div>
          ) : (
            <>
              {/* Current logo */}
              {currentLogoUrl && !imageSrc && (
                <div
                  style={{
                    display: "flex", alignItems: "center", gap: 14,
                    background: "#f9fafb", border: "1px solid #f3f4f6",
                    borderRadius: 12, padding: "12px 14px", marginBottom: 16,
                  }}
                >
                  <div
                    style={{
                      width: 56, height: 56, borderRadius: "50%",
                      overflow: "hidden", border: "2px solid #e5e7eb", flexShrink: 0,
                    }}
                  >
                    <img
                      src={currentLogoUrl}
                      alt="Current logo"
                      style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: "#111827" }}>Current logo</div>
                    <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 2 }}>Upload a new image below to replace it</div>
                  </div>
                  <button
                    type="button"
                    onClick={deleteLogo}
                    disabled={saving}
                    style={{
                      padding: "6px 12px", borderRadius: 8, flexShrink: 0,
                      border: "1.5px solid #fca5a5", background: "#fef2f2",
                      color: "#ef4444", fontSize: 12, fontWeight: 600,
                      cursor: saving ? "not-allowed" : "pointer",
                      display: "flex", alignItems: "center", gap: 6,
                      opacity: saving ? 0.6 : 1,
                    }}
                  >
                    <i className="fas fa-trash" style={{ fontSize: 11 }} />
                    Remove
                  </button>
                </div>
              )}

              {/* Upload zone */}
              {!imageSrc && (
                <>
                  <div
                    className="logo-upload-zone"
                    onClick={() => !saving && document.getElementById("logo-file-input")?.click()}
                  >
                    <i
                      className="fas fa-cloud-upload-alt"
                      style={{ fontSize: 22, color: "#d1d5db", display: "block", marginBottom: 6 }}
                    />
                    <div style={{ fontSize: 13, fontWeight: 600, color: "#6b7280" }}>
                      Click to select image
                    </div>
                    <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 3 }}>
                      PNG, JPG, or WEBP — max 8 MB
                    </div>
                  </div>
                  <input
                    id="logo-file-input"
                    type="file"
                    accept="image/png,image/jpeg,image/webp,image/gif"
                    onChange={onSelectFile}
                    disabled={saving}
                    style={{ display: "none" }}
                  />
                </>
              )}

              {/* Crop zone */}
              {imageSrc && (
                <>
                  <div
                    style={{
                      background: "#18181b", borderRadius: 12, overflow: "hidden",
                      display: "flex", width: "100%",
                      alignItems: "center", justifyContent: "center",
                      marginBottom: 12,
                    }}
                  >
                    <ReactCrop
                      crop={crop}
                      onChange={(c) => setCrop(c)}
                      onComplete={(c) => setCompletedCrop(c)}
                      aspect={1}
                      keepSelection
                      ruleOfThirds
                    >
                      <img
                        src={imageSrc}
                        alt="To crop"
                        onLoad={(e) => onImageLoaded(e.currentTarget)}
                        style={{ maxHeight: 360, width: "100%", objectFit: "contain" }}
                      />
                    </ReactCrop>
                  </div>
                  <div style={{ fontSize: 11, color: "#9ca3af", textAlign: "center" }}>
                    Drag the handles to adjust the crop area
                  </div>
                  <button
                    type="button"
                    onClick={() => { setImageSrc(""); setCrop(undefined); setCompletedCrop(undefined); }}
                    style={{
                      marginTop: 10, width: "100%", padding: "8px 0", borderRadius: 10,
                      border: "1.5px solid #e5e7eb", background: "#fff",
                      color: "#6b7280", fontSize: 13, fontWeight: 600,
                      cursor: "pointer", transition: "border-color 0.15s",
                    }}
                  >
                    <i className="fas fa-redo" style={{ marginRight: 6, fontSize: 11 }} />
                    Choose a different image
                  </button>
                </>
              )}
            </>
          )}
        </Modal.Body>

        <Modal.Footer style={{ display: "flex", gap: 10 }}>
          <button
            type="button"
            onClick={handleClose}
            disabled={saving}
            style={{
              flex: 1, padding: "11px 0", borderRadius: 10,
              border: "1.5px solid #e5e7eb", background: "#fff",
              color: "#374151", fontWeight: 700, fontSize: 15,
              cursor: saving ? "not-allowed" : "pointer",
              opacity: saving ? 0.6 : 1,
              transition: "border-color 0.15s",
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={uploadLogo}
            disabled={!canSave}
            style={{
              flex: 2, padding: "11px 0", borderRadius: 10, border: "none",
              background: canSave ? "linear-gradient(135deg, #c13d00 0%, #ff5100 100%)" : "#e5e7eb",
              color: canSave ? "#fff" : "#9ca3af",
              fontWeight: 700, fontSize: 15,
              cursor: canSave ? "pointer" : "not-allowed",
              display: "flex", alignItems: "center", justifyContent: "center",
              gap: 9, transition: "background 0.15s",
            }}
          >
            {saving ? (
              <>
                <i className="fas fa-spinner fa-spin" style={{ fontSize: 13 }} />
                Uploading…
              </>
            ) : (
              <>
                Save Logo
                <i className="fas fa-arrow-right" style={{ fontSize: 13 }} />
              </>
            )}
          </button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default UpdateClientLogo;
